/**
 * Utilities for DOM manipulation
 */
class DOMUtils {
  /**
   * Find element by selector within form or document
   */
  static findElement(selector, form = null) {
    return form
      ? form.querySelector(selector)
      : document.querySelector(selector);
  }

  /**
   * Get class prefix from element (e.g., 'auth-form' or 'my-profile-form')
   * @param {HTMLElement} element - Element to get prefix from
   * @param {string} suffix - Class suffix (e.g., '__field', '__message')
   * @returns {string} Class prefix
   */
  static getClassPrefix(element, suffix = '__field') {
    if (!element) return 'auth-form';

    const classes = element.className.split(' ');
    const classWithSuffix = classes.find((cls) => cls.includes(suffix));

    if (classWithSuffix) {
      return classWithSuffix.split(suffix)[0];
    }

    // Fallback: try to find any class with double underscore
    const classWithUnderscore = classes.find((cls) => cls.includes('__'));
    if (classWithUnderscore) {
      return classWithUnderscore.split('__')[0];
    }

    return 'auth-form'; // Default fallback
  }

  /**
   * Get field container and error element for input
   */
  static getFieldElements(input) {
    const field = input.closest('[class*="__field"]');
    if (!field) return { field: null, errorEl: null };

    const prefix = this.getClassPrefix(field, '__field');
    const errorEl = field.querySelector(`.${prefix}__error`);

    return { field, errorEl };
  }

  /**
   * Show error in form field
   */
  static showError(input, message, isHTML = false, autoClear = true) {
    const { field, errorEl } = this.getFieldElements(input);

    if (errorEl && field) {
      // Preserve default/custom message (for forms where error spans have static text)
      if (!errorEl.dataset.defaultMessage) {
        const defaultMessage = errorEl.textContent?.trim();
        if (defaultMessage) {
          errorEl.dataset.defaultMessage = defaultMessage;
        }
      }

      // Set message (automatically clears previous content)
      let resolvedMessage = message;
      if (message && typeof message === 'object') {
        if (message.message && message.message.trim() !== '') {
          resolvedMessage = message.message;
        } else if (message.key) {
          resolvedMessage = message.key;
        } else {
          resolvedMessage = '';
        }
      }

      if (isHTML) {
        errorEl.innerHTML = resolvedMessage;
      } else {
        errorEl.textContent = resolvedMessage;
      }

      // Add error class using detected prefix
      const prefix = this.getClassPrefix(field, '__field');
      field.classList.add(`${prefix}__field--error`);
    }

    // Remove error on input
    if (autoClear) {
      input.addEventListener('input', () => {
        this.clearError(input);
      }, { once: true });
    }
  }

  /**
   * Clear error from field
   */
  static clearError(input) {
    const { field, errorEl } = this.getFieldElements(input);

    if (field) {
      // Remove error class using detected prefix
      const prefix = this.getClassPrefix(field, '__field');
      field.classList.remove(`${prefix}__field--error`);
    }

    if (errorEl) {
      errorEl.textContent = errorEl.dataset.defaultMessage || '';
    }
  }

  /**
   * Set loading state for form
   */
  static setLoading(formSelector, isLoading, formElement = null) {
    let form;

    // If formSelector is already a DOM element, use it directly
    if (formSelector && typeof formSelector !== 'string') {
      form = formSelector;
    } else if (formSelector && formElement) {
      // If formSelector is a string and formElement is provided, search inside formElement
      form = formElement.querySelector(formSelector);
    } else if (formElement) {
      // If only formElement is provided, use it directly
      form = formElement;
    } else if (formSelector) {
      // If only formSelector (string) is provided, search in document
      form = document.querySelector(formSelector);
    }

    if (!form) return;

    // Find submit button, any button, or link
    const button = form.querySelector('button[type="submit"]')
      || form.querySelector('button')
      || form.querySelector('a[type="submit"]')
      || form.querySelector('a[class*="__button"]');

    if (!button) return;

    // Get prefix from button or form
    const prefix = this.getClassPrefix(button, '__button') || this.getClassPrefix(form, '__form') || 'auth-form';

    // Find button text and loader using prefix
    const buttonText = button.querySelector(`.${prefix}__button-text`);
    const loader = button.querySelector(`.${prefix}__loader`);

    // Handle button
    if (button.tagName === 'BUTTON') {
      if ('forceDisabled' in button.dataset) {
        button.disabled = true;
      } else {
        button.disabled = isLoading;
      }
    } else if (button.tagName === 'A') {
      // Handle link
      button.style.pointerEvents = isLoading ? 'none' : 'auto';
      button.style.opacity = isLoading ? '0.6' : '1';
    }

    // Toggle button text and loader
    if (buttonText) {
      buttonText.style.display = isLoading ? 'none' : 'block';
    }

    if (loader) {
      loader.style.display = isLoading ? 'inline-flex' : 'none';
    }
  }

  /**
   * Set message content and type
   */
  static setMessage(element, message, type = 'info', show = true) {
    if (!element) return;

    // Use innerHTML if message contains HTML tags, otherwise use textContent
    // Messages come from translations and are safe (controlled by developers)
    if (message && /<[a-z][\s\S]*>/i.test(message)) {
      // eslint-disable-next-line no-param-reassign
      element.innerHTML = message;
    } else {
      // eslint-disable-next-line no-param-reassign
      element.textContent = message;
    }

    // Get prefix from element
    const prefix = this.getClassPrefix(element, '__message');
    const messageClass = `${prefix}__message`;

    // eslint-disable-next-line no-param-reassign
    element.className = `${messageClass} ${messageClass}--${type}`;

    if (show) {
      // eslint-disable-next-line no-param-reassign
      element.style.display = 'block';
    }
  }

  /**
   * Show message
   */
  static showMessage(element, message, type = 'info') {
    this.setMessage(element, message, type, true);
  }

  /**
   * Update message (without changing display)
   */
  static updateMessage(element, message, type = 'info') {
    this.setMessage(element, message, type, false);
  }

  /**
   * Hide message
   */
  static hideMessage(element) {
    if (element) {
      // eslint-disable-next-line no-param-reassign
      element.style.display = 'none';

      // Get prefix from element and preserve base message class
      const prefix = this.getClassPrefix(element, '__message');
      // eslint-disable-next-line no-param-reassign
      element.className = `${prefix}__message`;
    }
  }

  /**
   * Get form field value
   */
  static getFieldValue(selector, form = null) {
    const element = this.findElement(selector, form);
    if (!element) return '';

    // For date inputs with IMask, check if date is actually filled
    if (element.classList.contains('form__date-input')) {
      // IMask stores instance on element._mask
      // eslint-disable-next-line no-underscore-dangle
      const mask = element._mask;
      if (mask && mask.unmaskedValue !== undefined) {
        // If date is not complete (less than 8 digits), return empty string
        // This prevents sending 'MM / DD / YYYY' placeholder to backend
        if (mask.unmaskedValue.length < 8) {
          return '';
        }
      }
    }

    return element.value.trim();
  }

  /**
   * Set form field value
   */
  static setFieldValue(selector, value, form = null) {
    const element = this.findElement(selector, form);
    if (element) {
      element.value = value;
    }
  }

  /**
   * Get multiple field values at once
   */
  static getFieldValues(selectors, form = null) {
    const values = {};
    Object.entries(selectors).forEach(([key, selector]) => {
      values[key] = this.getFieldValue(selector, form);
    });
    return values;
  }

  /**
   * Get current URL without parameters
   */
  static getCurrentPageUrl() {
    return `${window.location.origin}${window.location.pathname}`;
  }

  /**
   * Show preloader overlay
   */
  static showPreloader(form = null) {
    const preloader = form
      ? form.querySelector('#auth-form-preloader')
      : document.querySelector('#auth-form-preloader');
    if (preloader) {
      preloader.style.display = 'flex';
    }
  }

  /**
   * Hide preloader overlay
   */
  static hidePreloader(form = null) {
    const preloader = form
      ? form.querySelector('#auth-form-preloader')
      : document.querySelector('#auth-form-preloader');
    if (preloader) {
      preloader.style.display = 'none';
    }
  }
}

export default DOMUtils;
